import { useApolloClient } from '@apollo/client/react';
import { CheckIcon, ChevronDownIcon, ClipboardDocumentIcon, GlobeAltIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../../components/Button';
import { Modal } from '../../../components/Modal';
import { UserContext } from '../../../contexts/UserContext';
import { useUserRoleTranslations } from '../../../hooks/translations/useUserRoleTranslations';
import { ToastType, useToastNotifications } from '../../../hooks/useToastNotifications';
import { buildShareUrl } from '../../../share/shareMode';
import { T, useT } from '../../../translation/src';
import {
  Project,
  ProjectAccessLevel,
  UserRole,
  UserStatus,
  UsersDocument,
  useProjectRemoveGuestMutation,
  useProjectSetGuestAccessLevelMutation,
  useProjectShareQuery,
  useProjectShareResetMutation,
  useProjectShareSetMutation,
  useUsersQuery,
} from '../../../types/graphqlTypes';
import { InviteGuestsByEmail } from '../InviteGuestsByEmail';
import { AccessLevelSelect, DEFAULT_ACCESS_LEVEL } from '../ProjectAccessPicker';

const COPIED_FEEDBACK_MS = 2000;

/** Values of the "general access" select. */
const RESTRICTED = 'RESTRICTED';
const ANYONE_WITH_LINK = 'ANYONE_WITH_LINK';

const useTranslations = () => ({
  link: useT('link', { swc: true }),
  copy: useT('copy', { swc: true }),
  copied: useT('copied', { swc: true }),
  copyFailed: useT('could not copy the link', { swc: true }),
  restricted: useT('restricted', { swc: true }),
  anyoneWithLink: useT('anyone with the link', { swc: true }),
  generalAccess: useT('general access', { swc: true }),
  done: useT('done', { swc: true }),
});

interface ProjectShareModalProps {
  onClose: () => void;
  open?: boolean;
  project: Pick<Project, 'id' | 'name'>;
}

const initials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

/**
 * Sharing, laid out like a document editor's share dialog: add people at the top, who has access
 * in the middle, the link at the bottom. Every change applies immediately; Done just closes.
 */
const ProjectShareModal_: React.FC2<ProjectShareModalProps> = ({ onClose, open, project }) => {
  const translations = useTranslations();
  const roleTranslations = useUserRoleTranslations();
  const { organisationId = '' } = useParams();
  const currentUser = useContext(UserContext);
  const organisationName = currentUser.organisations.find((organisation) => organisation.id === organisationId)?.name ?? '';
  const [membersExpanded, setMembersExpanded] = useState(false);
  /** While addresses are being typed the dialog is just the invite form: the rest steps aside. */
  const [inviting, setInviting] = useState(false);
  const { showToast } = useToastNotifications();
  const apolloClient = useApolloClient();
  const { data, loading } = useProjectShareQuery({ variables: { projectId: project.id }, skip: !open });
  const { data: usersData } = useUsersQuery({ variables: { organisationId }, skip: !open });
  const [setShare, { loading: setting }] = useProjectShareSetMutation();
  const [resetShare, { loading: resetting }] = useProjectShareResetMutation();
  const [setGuestAccessLevel] = useProjectSetGuestAccessLevelMutation();
  const [removeProjectGuest] = useProjectRemoveGuestMutation();
  const [copied, setCopied] = useState(false);

  const share = data?.projectById.share;
  const linkEnabled = !!share?.enabled;
  const link = share?.token ? buildShareUrl(share.token) : '';
  const busy = loading || setting || resetting;

  const users = useMemo(() => usersData?.usersByOrganisationId ?? [], [usersData?.usersByOrganisationId]);
  const members = useMemo(() => users.filter((user) => user.role === UserRole.Admin || user.role === UserRole.Member).sort((a, b) => a.name.localeCompare(b.name)), [users]);
  const guestsWithAccess = useMemo(
    () =>
      users
        .filter((user) => user.role === UserRole.Guest && user.allowedProjectIds.includes(project.id))
        .map((user) => ({ ...user, level: user.projectAccess.find((access) => access.projectId === project.id)?.level ?? DEFAULT_ACCESS_LEVEL }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [users, project.id],
  );

  useEffect(() => {
    if (!copied) return;
    const timeout = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    return () => clearTimeout(timeout);
  }, [copied]);

  const onGeneralAccessChange = useCallback((value: string) => setShare({ variables: { projectId: project.id, enabled: value === ANYONE_WITH_LINK } }), [project.id, setShare]);
  const onReset = useCallback(() => {
    setCopied(false);
    return resetShare({ variables: { projectId: project.id } });
  }, [project.id, resetShare]);
  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      showToast({ type: ToastType.ERROR, title: translations.copyFailed, text: link });
    }
  }, [link, showToast, translations.copyFailed]);
  const onGuestLevelChange = useCallback(
    async (userId: string, level: ProjectAccessLevel | undefined) => {
      if (level === undefined) await removeProjectGuest({ variables: { projectId: project.id, userId } });
      else await setGuestAccessLevel({ variables: { projectId: project.id, userId, level } });
      await apolloClient.refetchQueries({ include: [UsersDocument] });
    },
    [apolloClient, project.id, removeProjectGuest, setGuestAccessLevel],
  );

  return (
    <Modal.Container onClose={onClose} open={open} panelClassName="w-full max-w-xl">
      <Modal.Header onClose={onClose} className="!mb-4">
        <T _str="share {name}" args={{ name: project.name }} swc />
      </Modal.Header>
      <Modal.Body className="flex flex-col gap-5">
        <InviteGuestsByEmail projectId={project.id} onActiveChange={setInviting} />

        {!inviting && (
          <section>
            <div className="mb-2 text-sm font-bold">
              <T _str="people with access" swc />
            </div>
            <div className="flex flex-col divide-y divide-gray-100 dark:divide-gray-800 max-h-[40vh] overflow-x-hidden overflow-y-auto dark:[color-scheme:dark]">
              {/* Members and admins are covered by their role: one row for the organisation, expandable to the people. */}
              <button type="button" onClick={() => setMembersExpanded((value) => !value)} aria-expanded={membersExpanded} className="flex items-center w-full gap-3 py-2 text-left rounded-md hover:bg-gray-50 dark:hover:bg-[#2c2c2c]">
                <div className="flex items-center justify-center w-8 h-8 text-xs font-bold text-white rounded-full shrink-0 bg-french-violet-600">{members.length}</div>
                <div className="flex flex-col flex-grow min-w-0">
                  <span className="text-sm font-medium truncate">
                    <T _str="{count} members of {organisation}" args={{ count: members.length, organisation: organisationName }} />
                  </span>
                  <span className="text-xs text-gray-500 truncate dark:text-gray-400">
                    <T _str="Members and admins can open every project of the organisation." />
                  </span>
                </div>
                <span className="flex items-center gap-1 text-xs shrink-0 text-neon-green-700 dark:text-neon-green-300">
                  <CheckIcon className="w-4 h-4" />
                  <T _str="full access" swc />
                </span>
                <ChevronDownIcon className={classNames('w-4 h-4 text-gray-500 transition-transform shrink-0', membersExpanded && 'rotate-180')} />
              </button>
              {membersExpanded &&
                members.map((member) => (
                  <div key={member.id} className="flex items-center gap-3 py-2 pl-11">
                    <div className="flex items-center justify-center w-6 h-6 text-[10px] font-bold text-white rounded-full shrink-0 bg-french-violet-400">{initials(member.name) || '?'}</div>
                    <div className="flex flex-col flex-grow min-w-0">
                      <span className="text-sm truncate">{member.name}</span>
                      <span className="text-xs text-gray-500 truncate dark:text-gray-400">{member.email}</span>
                    </div>
                    <span className="text-xs text-gray-500 shrink-0 dark:text-gray-400">{roleTranslations[member.role.toLowerCase()]}</span>
                  </div>
                ))}
              {guestsWithAccess.map((guest) => (
                <div key={guest.id} className="flex items-center gap-3 py-2">
                  <div className="flex items-center justify-center w-8 h-8 text-xs font-bold rounded-full shrink-0 bg-neon-green-300 text-eerie-black">{initials(guest.name) || '?'}</div>
                  <div className="flex flex-col flex-grow min-w-0">
                    <span className="text-sm font-medium truncate">
                      {guest.name}
                      {guest.status === UserStatus.InvitePending && (
                        <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                          (<T _str="invite pending" />)
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-gray-500 truncate dark:text-gray-400">{guest.email}</span>
                  </div>
                  <AccessLevelSelect value={guest.level} onChange={(level) => onGuestLevelChange(guest.id, level)} className="w-32 shrink-0" />
                </div>
              ))}
            </div>
          </section>
        )}

        {!inviting && (
          <section>
            <div className="mb-2 text-sm font-bold">{translations.generalAccess}</div>
            {/* Icon and dropdown share one row so their centres line up; the description hangs under the dropdown. */}
            <div className="flex items-center gap-3">
              <div
                className={classNames(
                  'flex items-center justify-center w-8 h-8 rounded-full shrink-0',
                  linkEnabled ? 'bg-neon-green-300/30 text-neon-green-700 dark:text-neon-green-300' : 'bg-gray-200 text-gray-600 dark:bg-[#404040] dark:text-gray-300',
                )}
              >
                {linkEnabled ? <GlobeAltIcon className="w-5 h-5" /> : <LockClosedIcon className="w-5 h-5" />}
              </div>
              {/* Native select with its own chevron: the browser's arrow is unreliable on a transparent, dark select. */}
              <div className="relative">
                <select
                  value={linkEnabled ? ANYONE_WITH_LINK : RESTRICTED}
                  onChange={(event) => onGeneralAccessChange(event.target.value)}
                  disabled={busy}
                  aria-label={translations.generalAccess}
                  className="py-1 pl-2 pr-8 text-sm font-medium bg-white bg-none border border-gray-300 rounded-md appearance-none cursor-pointer dark:bg-[#2c2c2c] dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 dark:[color-scheme:dark] [&>option]:bg-white [&>option]:text-black dark:[&>option]:bg-[#2c2c2c] dark:[&>option]:text-white disabled:opacity-50"
                >
                  <option value={RESTRICTED}>{translations.restricted}</option>
                  <option value={ANYONE_WITH_LINK}>{translations.anyoneWithLink}</option>
                </select>
                <ChevronDownIcon className="absolute w-4 h-4 text-gray-500 -translate-y-1/2 pointer-events-none right-2 top-1/2 dark:text-gray-400" />
              </div>
            </div>
            <div className="mt-1 ml-11 text-xs text-gray-500 dark:text-gray-400">
              {linkEnabled ? <T _str="Anyone on the internet with the link can view. No account needed. No editing, exporting or commenting." /> : <T _str="Only the people listed above can open this project." />}
            </div>
            {linkEnabled && link && (
              <div className="flex flex-col gap-2 mt-3 ml-11">
                <div className="flex items-stretch overflow-hidden border border-gray-300 rounded-md dark:border-gray-600 focus-within:border-french-violet-600">
                  <input readOnly value={link} onFocus={(event) => event.currentTarget.select()} aria-label={translations.link} className="flex-grow min-w-0 px-3 py-2 text-sm bg-transparent outline-none dark:text-white" />
                  <button
                    type="button"
                    onClick={onCopy}
                    disabled={busy}
                    className="flex items-center gap-1.5 px-3 text-sm font-medium border-l border-gray-300 whitespace-nowrap dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-[#404040] disabled:opacity-50"
                  >
                    {copied ? <CheckIcon className="w-4 h-4 text-neon-green-700 dark:text-neon-green-300" /> : <ClipboardDocumentIcon className="w-4 h-4" />}
                    <span>{copied ? translations.copied : translations.copy}</span>
                  </button>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <button type="button" onClick={onReset} disabled={busy} className="mr-1 font-medium underline text-french-violet-600 dark:text-french-violet-400 disabled:opacity-50">
                    <T _str="reset link" swc />
                  </button>
                  <T _str="Every copy handed out so far stops working." />
                </div>
              </div>
            )}
          </section>
        )}
      </Modal.Body>
      {!inviting && (
        <Modal.Footer className="justify-end">
          <Button variant="primary" size="xs" onClick={onClose}>
            <span className="whitespace-nowrap">{translations.done}</span>
          </Button>
        </Modal.Footer>
      )}
    </Modal.Container>
  );
};

export const ProjectShareModal = memo(ProjectShareModal_);
